import { CircularProgress, Typography, makeStyles, Grid, Button } from '@material-ui/core';
import { User, UserRole, getEditorialTypeLabel, ContentMarkedForDeletion, ContentType, getNodeTypeLabel } from '../../domain/domain';
import React, { useEffect, useState } from 'react';
import { ApplicationState } from '../../reducers';
import { compose } from "redux";
import { deleteContent, getMarkedForDeletionNodes, printMarkedForDeletionNodes } from '../../api/node';
import { connect, useDispatch } from "react-redux";
import { Link, useHistory } from 'react-router-dom';
import { ConfirmationDialog } from '../Dialogs/ConfirmationDialog';
import MUIDataTable from 'mui-datatables';
import { UiActionType } from '../../reducers/ui.reducer';
import { useErrorNotification } from '../../contexts/ErrorNotificationContext';

type Props = {
  currentUser: User
}

const useStyles = makeStyles(_ => ({
  root: {
    maxWidth: '80%',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: 20
  },
  columnTitle: {
    fontWeight: 'bold'
  },
  select: {
    width: 200
  },
  tableRow: {
    "&.MuiTableRow-hover": {
      '&:hover': {
        backgroundColor: '#89CFF0',
      },
    },
  },
}));

type TableRow = {
  citation: string,
  deletedContentType: string,
}

const MarkedForDeletionPage: React.FC<Props> = ({ currentUser }) => {
  const classes = useStyles();
  const history = useHistory();
  const { showError } = useErrorNotification();
  const [rows, setRows] = useState<ContentMarkedForDeletion[] | undefined>(undefined);
  const [selectedRows, setSelectedRows] = useState<any[] | undefined>([]);
  const [selectedRowsData, setSelectedRowsData] = useState<any[] | undefined>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const dispatch = useDispatch();

  function transformRows(rows: ContentMarkedForDeletion[]): TableRow[] {
    return rows.map(r => {
      let deletedContentType = "";
      if(r.contentType === ContentType.NODE) {
        deletedContentType = "Entire " + getNodeTypeLabel(r.node.nodeType);
      } else if(r.contentType === ContentType.ANNOTATION) {
        deletedContentType = "Annotation";
      }
      else if(r.contentType === ContentType.EDITORIAL_CONTENT) {
        deletedContentType = getEditorialTypeLabel(r.editorialContent!.editorialType);
      }
      return {
        citation: r.node.citation,
        heading: r.node.heading,
        deletedContentType: deletedContentType,
      }
    });
  }

  const columns = [
    {
      name: 'citation',
      label: 'Citation'
    },
    {
      name: 'heading',
      label: 'Heading'
    },
    {
      name: 'deletedContentType',
      label: 'Deleted Content Type',
    },
];

  useEffect(() => {
    getMarkedForDeletionNodes().then(response => {
      let newRows : ContentMarkedForDeletion[] = [];
      response.data.forEach(node => {
        if(node.markedForDeletion){
          newRows.push({
            contentType : ContentType.NODE,
            node : node,
            editorialContent : undefined,
            annotation : undefined
          })
        }
        node.editorialContent.forEach(editorialContent => {
          if(editorialContent.markedForDeletion){
            newRows.push({
              contentType : ContentType.EDITORIAL_CONTENT,
              node : node,
              editorialContent : editorialContent,
              annotation : undefined
            })
          }
        })
        if(node.annotations?.markedForDeletion) {
          newRows.push({
            contentType : ContentType.ANNOTATION,
            node : node,
            editorialContent : undefined,
            annotation : node.annotations})
        }
      });
      setRows(newRows);
    }).catch(error => {
      console.error(error);
    })
  }, []);

  useEffect(() => {
    if (currentUser.userRole !== UserRole.ADMINISTRATOR) {
      showError(
        'Access Denied',
        'You do not have permission to access this page.',
        'Only administrators can access this page.'
      );
    }
  }, [currentUser.userRole, showError, history]);

  if (currentUser.userRole !== UserRole.ADMINISTRATOR) {
    return (
      <div style={{ textAlign: "center" }} className="m-xsmall-top-2">
        <div className="m-xsmall-top-1">Access Denied. You do not have permission to access this page.</div>
      </div>
    );
  }

  if (rows === undefined) {
    return <CircularProgress />
  }

  return (
    <>
      <ConfirmationDialog
        open={deleteDialogOpen}
        title={"Are you sure you want to delete these " + selectedRows?.length + " rows?"}
        contentText=""
        handleNo = {() => {
            setDeleteDialogOpen(false);
        }}
        handleYes = {() => {
          let selectedRecords = selectedRows === undefined ? [] : selectedRows.map(rowIndex => rows[rowIndex]);
          deleteContent(selectedRecords).then(response => {
                let newRows : ContentMarkedForDeletion[] = [];
                for(let i = 0; i < rows.length; ++i){
                  if(selectedRows!.indexOf(i)<0){
                    newRows.push(rows[i]);
                  }
                }
                setRows(newRows);
                setSelectedRows([]);
                dispatch({ type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Deleted content", severity: "success" } });
            }).catch(error => {
                console.error(error);
                dispatch({ type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Internal server errror. Please try again later.", severity: "error" } });
            }).finally(() => {
                setDeleteDialogOpen(false);
            })
        }}
    />
    <Grid container direction='column' className={`${classes.root} flex-gap-4`}>
      <Grid item>
        <Link to='/admin'>Administrator Page</Link>
      </Grid>
      <Grid item>
        <Typography variant='h2' style={{fontWeight: 'bold'}}>Delete List</Typography>
      </Grid>
      <Grid item>
        <Grid container direction='row' className="flex-gap-1">
          <Grid item>
              <Button variant='contained' color='primary' disabled={selectedRows?.length === 0} onClick={() => {
                setDeleteDialogOpen(true);
              }}>
                Delete Selected Content
              </Button>
          </Grid>
          <Grid item>
              <Button variant='contained' color='primary' disabled={selectedRowsData?.length === 0} onClick={() => {
                  if (selectedRowsData === undefined) {
                    return;
                  }
                  let selectedRecords = selectedRows === undefined ? [] : selectedRows.map(rowIndex => rows[rowIndex]);
                  printMarkedForDeletionNodes(selectedRecords).then(response => {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    let finalName = "marked_for_deletion_export.docx";
                    link.setAttribute('download', finalName);
                    document.body.appendChild(link);
                    link.click();
                  }).catch(error => {
                    console.error(error);
                  })
              }}>
                Print
              </Button>
          </Grid>
        </Grid>
      </Grid>
      <MUIDataTable
        title={""}
        columns={columns}
        data={transformRows(rows)}
        options={{
            filter: false,
            search: true,
            searchAlwaysOpen: true,
            print: false,
            download: false,
            responsive: 'standard',
            viewColumns: false,
            selectableRowsHeader: true,
            selectableRows: 'multiple',
            selectToolbarPlacement: 'above',
            customToolbarSelect: () => null,
            rowsSelected: selectedRows,
            enableNestedDataAccess: ".",
            onColumnSortChange: (changedColumn: string, direction: string) => {
            },
            onRowSelectionChange: (rowsSelectedData, allRows, rowsSelected) => {
                setSelectedRows(rowsSelected);
                setSelectedRowsData(allRows);
            },
            elevation: 0,
            rowsPerPageOptions: [10, 50, 100, 200],
            setTableProps: () => {
                return {
                    size: "small",
                    padding: "1px"
                }
            }
        }}
    />
    </Grid>
    </>
    )
}

const mapStateToProps = (state: ApplicationState) => {
  return {
    currentUser: state.user.currentUser
  };
};

export default compose<any>(
  connect(mapStateToProps, null)
)(MarkedForDeletionPage);